/**
 * MeasuredContainer — render children only once the wrapper has a real
 * (non-zero) measured size.
 *
 * Why (BL-018): recharts' `ResponsiveContainer` warns
 * "The width(-1) and height(-1) of chart should be greater than 0"
 * whenever it mounts inside a container that is 0-sized on first paint
 * — typical for Radix Sheets/Dialogs mid-open-animation and for tabs
 * that mount content before layout. Sprinkling `minWidth={1}
 * minHeight={1}` on every chart merely hid the warning while still
 * rendering a 1px chart frame on the bad first pass. This wrapper
 * fixes the root cause: charts (or anything size-dependent) mount only
 * after a ResizeObserver reports a real box, so the first render is
 * already correctly sized.
 *
 * The wrapper div carries the sizing styles/classNames; children render
 * into exactly the same box once measured, so there is no layout shift.
 *
 * Children may also be a function of the measured size — for a drawing that
 * lays itself out from its box (a radar choosing the largest radius its axis
 * labels leave room for). The box is then observed for as long as it is
 * mounted, so the drawing follows a drawer or card that resizes.
 */

import * as React from 'react';

import { cn } from '../lib/utils';

export interface MeasuredSize {
  width: number;
  height: number;
}

export interface MeasuredContainerProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'children'
> {
  /** Rendered only after the container measures > 0 in both axes. */
  children: React.ReactNode | ((size: MeasuredSize) => React.ReactNode);
}

export function MeasuredContainer({
  children,
  className,
  ...divProps
}: MeasuredContainerProps): React.JSX.Element {
  const ref = React.useRef<HTMLDivElement>(null);
  const [size, setSize] = React.useState<MeasuredSize | null>(null);
  const followSize = typeof children === 'function';

  React.useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;

    const check = (width: number, height: number) => {
      if (width > 0 && height > 0) {
        setSize((current) =>
          current && current.width === width && current.height === height
            ? current
            : { width, height }
        );
        return true;
      }
      return false;
    };

    // Already laid out (common outside dialogs) — render synchronously,
    // no observer round-trip, no flicker.
    const rect = node.getBoundingClientRect();
    const measuredNow = check(rect.width, rect.height);
    if (measuredNow && !followSize) return;

    if (typeof ResizeObserver === 'undefined') {
      // Ancient/JSDOM environments: render immediately rather than never.
      if (!measuredNow) setSize({ width: rect.width, height: rect.height });
      return;
    }
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (check(entry.contentRect.width, entry.contentRect.height) && !followSize) {
          observer.disconnect();
          return;
        }
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [followSize]);

  return (
    <div ref={ref} className={cn(className)} {...divProps}>
      {size ? (typeof children === 'function' ? children(size) : children) : null}
    </div>
  );
}
